'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import identity from 'lodash/identity';
import type React from 'react';
import { useEffect, useState } from 'react';
import Hint from '../hint';
import Input from '../internal/input';
import usePinInputRefs from './hooks/use-pin-input-refs';
import { INPUT_FIELDS_COUNT, pins } from './pin-input.constants';
import { getNextValue, isNumber } from './pin-input.utils';
export type PinInputProps = {
  hasError?: boolean;
  hint?: string;
  onComplete: (value: string) => void;
  testID?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  defaultValue?: string;
};
const PinInput = t0 => {
  const $ = _c(35);
  const {
    hasError: t1,
    hint,
    onComplete,
    testID,
    disabled,
    autoFocus,
    defaultValue: t2
  } = t0;
  const hasError = t1 === undefined ? false : t1;
  const defaultValue = t2 === undefined ? "" : t2;
  let t3;
  if ($[0] !== defaultValue) {
    t3 = () => Array.from(defaultValue).slice(0, INPUT_FIELDS_COUNT);
    $[0] = defaultValue;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [enteredPin, setEnteredPin] = useState(t3);
  const pinInputs = usePinInputRefs(INPUT_FIELDS_COUNT);
  let t4;
  let t5;
  if ($[2] !== autoFocus || $[3] !== disabled || $[4] !== pinInputs) {
    t4 = () => {
      const firstPinInput = pinInputs.get(0);
      if (firstPinInput && !disabled && autoFocus) {
        firstPinInput.focus();
      }
    };
    t5 = [autoFocus, disabled, pinInputs];
    $[2] = autoFocus;
    $[3] = disabled;
    $[4] = pinInputs;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  if ($[7] !== enteredPin) {
    t6 = (nextValue, pinIndex) => {
      const nextValues = [...enteredPin];
      nextValues[pinIndex] = nextValue;
      setEnteredPin(nextValues);
      return nextValues;
    };
    $[7] = enteredPin;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const updatePin = t6;
  let t7;
  if ($[9] !== onComplete || $[10] !== pinInputs) {
    t7 = (pin, index) => {
      const isLastIndex = index === INPUT_FIELDS_COUNT - 1;
      const areAllTheFieldsFilled = pin.length === INPUT_FIELDS_COUNT && Array.from(pin).every(identity);
      if (isLastIndex && areAllTheFieldsFilled) {
        onComplete(pin);
      } else {
        const nextInput = pinInputs.next(index);
        if (nextInput) {
          nextInput.focus();
          nextInput.select();
        }
      }
    };
    $[9] = onComplete;
    $[10] = pinInputs;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const moveToNextOrComplete = t7;
  let t8;
  if ($[12] !== enteredPin || $[13] !== moveToNextOrComplete || $[14] !== updatePin) {
    t8 = pinIndex_0 => event => {
      const eventValue = event.target.value;
      const currentValue = enteredPin[pinIndex_0];
      const nextValue_0 = getNextValue(currentValue, eventValue);
      if (nextValue_0 === "") {
        updatePin("", pinIndex_0);
      }
      const wasPinPastedOrAutoCompleted = eventValue.length > 2;
      if (wasPinPastedOrAutoCompleted) {
        if (isNumber(eventValue)) {
          const nextPin = Array.from(eventValue).filter(_temp);
          setEnteredPin(nextPin);
          moveToNextOrComplete(nextPin.join(""), nextPin.length - 1);
        }
      } else {
        if (isNumber(nextValue_0)) {
          const nextPin_0 = updatePin(nextValue_0, pinIndex_0);
          moveToNextOrComplete(nextPin_0.join(""), pinIndex_0);
        }
      }
    };
    $[12] = enteredPin;
    $[13] = moveToNextOrComplete;
    $[14] = updatePin;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const handleChange = t8;
  let t9;
  if ($[16] !== pinInputs || $[17] !== updatePin) {
    t9 = pinIndex_1 => event_0 => {
      const element = event_0.target as HTMLInputElement;
      if (event_0.key === "Backspace" && element.value === "") {
        const previousInput = pinInputs.previous(pinIndex_1);
        if (previousInput) {
          updatePin("", pinIndex_1 - 1);
          previousInput.focus();
        }
      }
    };
    $[16] = pinInputs;
    $[17] = updatePin;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const handleKeyDown = t9;
  let t10;
  if ($[19] !== disabled || $[20] !== enteredPin || $[21] !== handleChange || $[22] !== handleKeyDown || $[23] !== hasError || $[24] !== pinInputs.refs) {
    t10 = pins.map((_pinPosition, pinIndex_2) => {
      const key = pinIndex_2;
      const isIndexDisabled = pinIndex_2 > enteredPin.length;
      return <Input autoComplete="one-time-code" className={cx("fp-pin-input fp-custom-appearance text-center", {
        "pointer-events-none": disabled || isIndexDisabled
      })} data-nid-target={`otp${pinIndex_2 + 1}`} data-dd-action-name={`otp:click-${pinIndex_2 + 1}`} hasError={hasError} inputMode="numeric" key={key} onChange={handleChange(pinIndex_2)} onKeyDown={handleKeyDown(pinIndex_2)} placeholder="" ref={pinInputs.refs[pinIndex_2]} required={true} type="tel" value={enteredPin[pinIndex_2] || ""} width="40px" />;
    });
    $[19] = disabled;
    $[20] = enteredPin;
    $[21] = handleChange;
    $[22] = handleKeyDown;
    $[23] = hasError;
    $[24] = pinInputs.refs;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10) {
    t11 = <div className="flex gap-2">{t10}</div>;
    $[26] = t10;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== hasError || $[29] !== hint) {
    t12 = !!hint && <Hint hasError={hasError}>{hint}</Hint>;
    $[28] = hasError;
    $[29] = hint;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== t11 || $[32] !== t12 || $[33] !== testID) {
    t13 = <div data-testid={testID}>{t11}{t12}</div>;
    $[31] = t11;
    $[32] = t12;
    $[33] = testID;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
};
export default PinInput;
function _temp(_, index_0) {
  return index_0 < INPUT_FIELDS_COUNT;
}